import { avancementChecklist, lireDuree, lirePeriode, lireTaux } from '@taki/data-model';
import { formatDeColonne, liensMultiples } from './champs-types.js';
import type { Cell, Column, EditorKind, Option, ReglagesDeColonne, ViewBinding } from './contract.js';
import type { TypeDeChamp } from './fields.js';

export interface QueryChoice extends Option {
  /** Une personne : l'avatar, avec sa photo quand elle en a une. */
  avatar?: { src?: string | null };
  /** Archivee : elle se lit dans une puce, elle ne se propose plus. */
  archive?: boolean;
}
export interface QueryField {
  key: string;
  label: string;
  kind?: EditorKind | 'boolean';
  options?: QueryChoice[];
  /** Le champ designe une fiche (annuaire) : la valeur est son id. */
  lien?: boolean;
  /** Le champ designe une personne. */
  personne?: boolean;
  /** L'id de la personne connectee dans l'annuaire du champ : ce que « Moi » devient. */
  moi?: string;
  /** Le mot d'une valeur que l'annuaire ne connait pas, jamais l'id. */
  inconnu?: string;
  /**
   * LE TYPE DU CHAMP DECLARE (`Column.valueType`), quand il y en a un : c'est
   * lui qui choisit les conditions des types du 27/10/2026, editeur ou non.
   */
  type?: TypeDeChamp;
  /**
   * LES REGLAGES DE LA COLONNE (`Column.reglages`) : le FORMAT d'un nombre (une
   * duree se saisit « 2 h 30 », `formatDuNombre`), et `plusieurs` qui fait
   * d'un `lien` une liste d'ids (« contient la fiche »).
   */
  reglages?: ReglagesDeColonne;
}

/**
 * LES TYPES QUI SE FILTRENT COMME DES NOMBRES : la valeur stockee EST un
 * nombre (des minutes pour une duree, de 0 a 100 pour un pourcentage, un
 * montant en unites de sa devise : tous des `number`), donc « est superieur
 * a » et « est inferieur a » ont leur sens d'ordre.
 */
const TYPES_NUMERIQUES: ReadonlySet<string> = new Set(['number', 'note', 'calcul']);

/** Ce champ est-il une liste de liens (un `lien` qui porte `plusieurs`) ? */
const enListeDeLiens = (field: QueryField): boolean => liensMultiples(field.type, field.reglages);
/** Ce champ est-il un nombre au format duree (des minutes, saisies « 2 h 30 ») ? */
export const enDuree = (field: QueryField): boolean =>
  (field.type === 'number' || field.type === 'calcul' || (field.type === undefined && field.kind === 'number'))
  && formatDeColonne(field.type ?? 'number', field.reglages) === 'duree';

/** Ce champ est-il un nombre au format taux (une fraction, saisie « 21 % » ou « 21 ») ? */
export const enTaux = (field: QueryField): boolean =>
  (field.type === 'number' || field.type === 'calcul' || (field.type === undefined && field.kind === 'number'))
  && formatDeColonne(field.type ?? 'number', field.reglages) === 'taux';

/** Les deux etats d'une liste de controle qu'un filtre choisit. */
export const ETATS_DE_CHECKLIST: readonly QueryChoice[] = [
  { value: 'terminee', label: 'terminée', tone: 'neutral' },
  { value: 'en-cours', label: 'pas terminée', tone: 'neutral' },
];

/** Ce champ se compare-t-il comme un nombre ? */
const numerique = (field: QueryField): boolean =>
  field.kind === 'number' || (field.type !== undefined && TYPES_NUMERIQUES.has(field.type));
export type FieldOperator = 'contains' | 'eq' | 'in' | 'neq' | 'gt' | 'lt' | 'empty' | 'filled';
export const VALEUR_MOI = '~moi';
const SEPARATEUR = '\n';
export const valeursDe = (value: string): string[] => value.split(SEPARATEUR).filter((item) => item !== '');
export const joindreValeurs = (values: readonly string[]): string => values.join(SEPARATEUR);
export interface FieldCondition {
  id: string;
  field: string;
  operator: FieldOperator;
  value: string;
}
export const FIELD_OPERATORS: Record<FieldOperator, string> = {
  contains: 'contient', eq: 'est', in: 'est l’un de', neq: "n’est pas", gt: 'est supérieur à',
  lt: 'est inférieur à', empty: 'est vide', filled: 'est renseigné',
};
export function queryColumns<T>(bindings: readonly ViewBinding<T>[]): Column<T>[] {
  const fields = new Map<string, Column<T>>();
  for (const binding of bindings) {
    for (const column of binding.fieldColumns ?? binding.columns ?? []) {
      if (!fields.has(column.key)) fields.set(column.key, column);
    }
  }
  return [...fields.values()];
}
export function fieldOperators(field: QueryField): FieldOperator[] {
  /* LES TYPES DU 27/10/2026 ont leurs conditions : une periode « est en cours
     le », « commence apres », « finit avant » ; des etiquettes et des liens
     « contiennent » ; une liste de controle « est terminee ». */
  if (field.type === 'periode') return ['contains', 'gt', 'lt', 'empty', 'filled'];
  if (field.type === 'etiquettes') return ['contains', 'neq', 'empty', 'filled'];
  if (enListeDeLiens(field)) return field.options !== undefined ? ['in', 'neq', 'empty', 'filled'] : ['contains', 'empty', 'filled'];
  if (field.type === 'checklist') return ['eq', 'empty', 'filled'];
  if (numerique(field) || field.kind === 'date' || field.kind === 'datetime') {
    return ['eq', 'neq', 'gt', 'lt', 'empty', 'filled'];
  }
  if (field.kind === 'boolean') return ['eq', 'neq', 'empty', 'filled'];
  return field.options !== undefined ? ['eq', 'in', 'neq', 'empty', 'filled'] : ['contains', 'eq', 'neq', 'empty', 'filled'];
}
/**
 * LE CHAMP DE SAISIE D'UNE CONDITION : un jour pour une periode (« en cours
 * le »), du texte pour une duree (« 2 h 30 », lue comme a la fiche) ou un taux
 * (« 21 % »), un nombre
 * pour les autres types numeriques.
 */
export function typeDeSaisie(field: QueryField): 'number' | 'date' | 'datetime-local' | 'text' {
  if (field.type === 'periode' || field.kind === 'date') return 'date';
  if (field.kind === 'datetime') return 'datetime-local';
  if (enDuree(field) || enTaux(field)) return 'text';
  return numerique(field) ? 'number' : 'text';
}

export function operatorLabel(operator: FieldOperator, field?: QueryField): string {
  if (field?.type === 'periode') {
    if (operator === 'contains') return 'est en cours le';
    if (operator === 'gt') return 'commence après';
    if (operator === 'lt') return 'finit avant';
  }
  if (field?.type === 'etiquettes' && operator === 'neq') return 'ne contient pas';
  if (field !== undefined && enListeDeLiens(field)) {
    if (operator === 'in') return 'contient la fiche';
    if (operator === 'neq') return 'ne contient pas';
  }
  if (field?.kind === 'date' || field?.kind === 'datetime') {
    if (operator === 'gt') return 'est après';
    if (operator === 'lt') return 'est avant';
  }
  return FIELD_OPERATORS[operator];
}
export const searchField = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
function libelleDeValeur(value: string, field: QueryField): string {
  const trouve = field.options?.find((option) => option.value === value)?.label;
  if (trouve !== undefined) return trouve;
  return value === VALEUR_MOI ? 'Moi' : field.inconnu ?? value;
}
export function conditionLabel(condition: FieldCondition, field: QueryField): string {
  const value = condition.operator === 'in'
    ? valeursDe(condition.value).map((item) => libelleDeValeur(item, field)).join(', ')
    : libelleDeValeur(condition.value, field);
  return `${field.label} ${operatorLabel(condition.operator, field)}${condition.operator === 'empty' || condition.operator === 'filled' ? '' : ` ${value}`}`;
}
/** Une valeur de condition sur un nombre de ce type : une duree se saisit « 2 h 30 » et un taux « 21 % » comme a la fiche. */
function nombreDeCondition(field: QueryField, saisie: string): number {
  if (enDuree(field)) return lireDuree(saisie) ?? Number.NaN;
  if (enTaux(field)) return lireTaux(saisie) ?? Number.NaN;
  return Number(saisie.replace(',', '.'));
}

/** Les elements d'une liste, une ligne chacun (`lignesDeCellule`, sans couper a la virgule). */
const lignes = (text: string): string[] => text.split(/\r?\n/).map((item) => item.trim()).filter((item) => item !== '');

export function matchesField(value: Cell, condition: FieldCondition, field: QueryField): boolean {
  const empty = value == null || value === '';
  if (condition.operator === 'empty') return empty;
  if (condition.operator === 'filled') return !empty;
  if (empty) return false;
  const text = value instanceof Date ? value.toISOString() : String(value);
  if (field.type === 'periode') {
    const periode = lirePeriode(text);
    const jour = condition.value.slice(0, 10);
    if (periode === null || jour === '') return false;
    if (condition.operator === 'contains') return periode.debut <= jour && jour <= periode.fin;
    if (condition.operator === 'gt') return periode.debut > jour;
    if (condition.operator === 'lt') return periode.fin < jour;
    return false;
  }
  if (field.type === 'etiquettes' || (enListeDeLiens(field) && condition.operator === 'contains')) {
    const cherche = searchField(condition.value.trim());
    const trouve = lignes(text).some((item) => searchField(item).includes(cherche));
    return condition.operator === 'neq' ? !trouve : trouve;
  }
  if (enListeDeLiens(field)) {
    const ids = lignes(text);
    const attendues = (condition.operator === 'in' ? valeursDe(condition.value) : [condition.value])
      .map((item) => (item === VALEUR_MOI ? field.moi : item))
      .filter((item): item is string => item !== undefined);
    const contient = attendues.some((id) => ids.includes(id));
    return condition.operator === 'neq' ? !contient : contient;
  }
  if (field.type === 'checklist') {
    const { faits, total } = avancementChecklist(lignes(text));
    const terminee = total > 0 && faits === total;
    return condition.value === 'terminee' ? terminee : !terminee;
  }
  /* UN NOMBRE SANS EDITEUR (un calcul), une note, ou une DUREE (« 2 h » se lit
     en minutes) : la comparaison des nombres, condition relue selon le format. */
  if (numerique(field) && (field.kind !== 'number' || enDuree(field) || enTaux(field))) {
    const a = Number(value);
    const b = nombreDeCondition(field, condition.value);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    if (condition.operator === 'eq' || condition.operator === 'in') return a === b;
    if (condition.operator === 'neq') return a !== b;
    if (condition.operator === 'gt') return a > b;
    if (condition.operator === 'lt') return a < b;
    return false;
  }
  const actual = searchField(field.kind === 'date' ? text.slice(0, 10) : text);
  const expected = searchField(condition.value);
  const egal = (attendu: string): boolean => field.kind === 'multiselect' ? text.split(/[\n,]/).map((item) => item.trim()).includes(attendu)
    : field.kind === 'number' ? Number(value) === Number(attendu)
    : field.kind === 'datetime' ? new Date(text).getTime() === new Date(attendu).getTime()
    : actual === searchField(attendu);
  const attendues = (condition.operator === 'in' ? valeursDe(condition.value) : [condition.value])
    .map((item) => item === VALEUR_MOI ? field.moi : item)
    .filter((item): item is string => item !== undefined);
  const equals = attendues.some(egal);
  if (condition.operator === 'contains') return actual.includes(expected);
  if (condition.operator === 'eq' || condition.operator === 'in') return equals;
  if (condition.operator === 'neq') return !equals;
  const a = field.kind === 'number' ? Number(value) : field.kind === 'date' || field.kind === 'datetime' ? new Date(text).getTime() : actual;
  const b = field.kind === 'number' ? Number(condition.value) : field.kind === 'date' || field.kind === 'datetime' ? new Date(condition.value).getTime() : expected;
  return condition.operator === 'gt' ? a > b : a < b;
}
